import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { AuthApi } from '../services/authApi';
import { AuthenticationService } from '../services/authenticationService';
import { UrlParsingService } from '../services/urlParsingService';
import { isAuthError } from '../types/errors.types';

export interface DeviceVerificationState {
  userCode: string;
  loading: boolean;
  success: boolean;
  error: string | null;
  msalInitialized: boolean;
}

export interface DeviceVerificationActions {
  setUserCode: (code: string) => void;
  verifyDevice: () => Promise<void>;
  clearError: () => void;
}

export function useDeviceVerification(): DeviceVerificationState & DeviceVerificationActions {
  const [state, setState] = useState<DeviceVerificationState>({
    userCode: '',
    loading: false,
    success: false,
    error: null,
    msalInitialized: false,
  });

  const { instance } = useMsal();
  const authService = AuthenticationService.getInstance();

  useEffect(() => {
    initializeMsal();
  }, [instance]);

  useEffect(() => {
    if (state.msalInitialized && !state.success) {
      handleInitialLoad();
    }
  }, [state.msalInitialized, state.success]);

  const initializeMsal = async (): Promise<void> => {
    try {
      await instance.initialize();
      authService.initialize(instance);
      setState(prev => ({ ...prev, msalInitialized: true }));
    } catch (error) {
      console.error('MSAL initialization failed:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to initialize authentication system',
        msalInitialized: false,
      }));
    }
  };

  const handleInitialLoad = async (): Promise<void> => {
    // Extract device code from URL
    const deviceCode = UrlParsingService.getDeviceCodeFromUrl();
    if (deviceCode) {
      setState(prev => ({ ...prev, userCode: deviceCode }));
    }

    // Check for authentication errors in URL
    const authError = UrlParsingService.hasAuthenticationError();
    if (authError) {
      setState(prev => ({
        ...prev,
        error: authError.description || authError.error,
      }));
      return;
    }

    // Handle redirect return
    await handleRedirectReturn();

    // Attempt auto-verification if device code is present and user is authenticated
    if (deviceCode && authService.isUserAuthenticated()) {
      await attemptAutoVerification(deviceCode);
    }
  };

  const handleRedirectReturn = async (): Promise<void> => {
    try {
      const pendingCode = sessionStorage.getItem('pending_device_code');
      
      if (authService.isUserAuthenticated() && pendingCode) {
        sessionStorage.removeItem('pending_device_code');
        
        const scopes = [
          'api://6ef233b2-6c49-4272-a072-5bc6ae2855e8/access_as_user',
          'openid',
          'profile',
          'email'
        ];

        const result = await authService.acquireTokenSilently(scopes);
        
        if (!result.tokens.idToken) {
          throw new Error('No ID token received');
        }
        
        await AuthApi.verifyDeviceCode(pendingCode, result.tokens.idToken);
        
        // Save authentication data
        authService.saveAuthenticationData(result, 'device');
        setState(prev => ({ ...prev, success: true }));
        
        // Notify parent if we're in an iframe - no redirect for device verification
        if (window.parent !== window) {
          window.parent.postMessage({ 
            type: 'DEVICE_AUTH_SUCCESS', 
            tokens: result.tokens,
            userInfo: result.userInfo 
          }, '*');
        }
      }
    } catch (error) {
      console.error('Device verification failed after redirect:', error);
      
      let errorMessage = 'Authentication failed after redirect. Please try again.';
      if (isAuthError(error)) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setState(prev => ({ ...prev, error: errorMessage }));
    }
  };

  const attemptAutoVerification = async (deviceCode: string): Promise<void> => {
    if (!authService.isUserAuthenticated()) {
      return;
    }

    setState(prev => ({ ...prev, loading: true }));

    try {
      const scopes = [
        'api://6ef233b2-6c49-4272-a072-5bc6ae2855e8/access_as_user',
        'openid',
        'profile',
        'email'
      ];

      const result = await authService.acquireTokenSilently(scopes);
      
      if (!result.tokens.idToken) {
        throw new Error('No ID token received');
      }
      
      await AuthApi.verifyDeviceCode(deviceCode, result.tokens.idToken);
      
      // Save authentication data for auto-verification
      authService.saveAuthenticationData(result, 'device');
      
      setState(prev => ({
        ...prev,
        loading: false,
        success: true,
      }));
      
      // Notify parent if we're in an iframe - no redirect for device verification
      if (window.parent !== window) {
        window.parent.postMessage({ 
          type: 'DEVICE_AUTH_SUCCESS', 
          tokens: result.tokens,
          userInfo: result.userInfo 
        }, '*');
      }

    } catch (error) {
      console.error('Auto-verification failed:', error);
      setState(prev => ({ ...prev, loading: false }));
      // Don't set error for auto-verification failure - let user try manually
    }
  };

  const verifyDevice = async (): Promise<void> => {
    if (!state.userCode.trim()) {
      setState(prev => ({ ...prev, error: 'Please enter a valid user code' }));
      return;
    }

    if (!UrlParsingService.validateUserCode(state.userCode)) {
      setState(prev => ({ ...prev, error: 'Invalid user code format' }));
      return;
    }

    if (!state.msalInitialized) {
      setState(prev => ({
        ...prev,
        error: 'Authentication system is still initializing. Please wait a moment and try again.',
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const formattedCode = UrlParsingService.formatUserCode(state.userCode);

      // Check if user is already authenticated (SSO)
      if (authService.isUserAuthenticated()) {
        const scopes = [
          'api://6ef233b2-6c49-4272-a072-5bc6ae2855e8/access_as_user',
          'openid',
          'profile',
          'email'
        ];

        const result = await authService.acquireTokenSilently(scopes);
        
        if (!result.tokens.idToken) {
          throw new Error('No ID token received');
        }
        
        await AuthApi.verifyDeviceCode(formattedCode, result.tokens.idToken);
        
        // Save authentication data
        authService.saveAuthenticationData(result, 'device');
        
        setState(prev => ({
          ...prev,
          loading: false,
          success: true,
        }));
        
        // Notify parent if we're in an iframe - no redirect for device verification
        if (window.parent !== window) {
          window.parent.postMessage({ 
            type: 'DEVICE_AUTH_SUCCESS', 
            tokens: result.tokens,
            userInfo: result.userInfo 
          }, '*');
        }
        return;
      }

      // No existing auth - redirect to Azure AD
      sessionStorage.setItem('pending_device_code', formattedCode);
      
      await instance.loginRedirect({
        scopes: [
          'api://6ef233b2-6c49-4272-a072-5bc6ae2855e8/access_as_user',
          'openid',
          'profile',
          'email'
        ]
      });

      // Code after loginRedirect won't execute as the page will redirect

    } catch (error) {
      console.error('Device verification failed:', error);
      
      let errorMessage = 'Authentication failed. Please try again.';
      if (isAuthError(error)) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  };

  const setUserCode = (code: string): void => {
    setState(prev => ({ ...prev, userCode: code }));
  };

  const clearError = (): void => {
    setState(prev => ({ ...prev, error: null }));
  };

  return {
    ...state,
    setUserCode,
    verifyDevice,
    clearError,
  };
}